import * as React from "react"
import * as semver from "semver"
import {
  Spacer,
  Notification,
  ToggleCheckbox,
  Link,
  ThemeCss,
  Theme,
} from "gatsby-interface"
import { MdArrowForward, MdInfoOutline, MdInfo } from "react-icons/md"
import { ImageCdnUsage } from "./ImageCdnUsage"
import { useFlags } from "@modules/featureFlags"
import { useSiteDetailsFieldsFragment } from "@modules/site/shared/fragments.generated"
import { FormattedMessage } from "@modules/locales"
import { imageCdnIntegrationConfig as text } from "@modules/locales/default.js"
import { useSitePlatformLimitSummariesQuery } from "@modules/site/queries.generated"
import { supportedImageCdnPlugins } from "./shared/constants"
import { useToggleToggleImageCdn } from "../hooks/useToggleImageCdn"
import { useSiteDependencyInfo } from "../hooks/useSiteDependencyInfo"
import { useSatisfiesDependencyRange } from "@modules/site/details/hooks/useSatisfiesDependencyRange"

const wrapperStyle: ThemeCss = theme => ({
  display: `flex`,
  flexDirection: `column`,
  gap: theme.space[5],
})

const innerWrapperStyle: ThemeCss = theme => ({
  display: `flex`,
  alignItems: `flex-start`,
  justifyContent: `space-between`,
  gap: theme.space[5],
})

export type ImageCdnProps = {
  siteId: string
  organizationId: string
}

export function ImageCdn({ siteId, organizationId }: ImageCdnProps) {
  const { data: siteDetails } = useSiteDetailsFieldsFragment(siteId)
  const { toggleImageCdn, hideNotification, notificationVisible, errorAlert } =
    useToggleToggleImageCdn(siteId)
  const { flags } = useFlags()
  const { data: limitSummariesData } = useSitePlatformLimitSummariesQuery({
    variables: {
      workspaceId: organizationId,
      siteId,
    },
    fetchPolicy: "cache-and-network",
  })
  const imageCdnEnabled = siteDetails?.imageCdnEnabled || false

  const { siteDependencies, isCompatibleGatsbyVersion } =
    useSiteDependencyInfo(siteId)

  const { satisfiesDependencyRange } = useSatisfiesDependencyRange({
    siteId,
    dependencyName: `gatsby-plugin-utils`,
    dependencyRange: `>=3.17.1`,
  })

  const platformLimitSummaries = (
    limitSummariesData?.platformLimitSummaries || []
  ).filter(Boolean)

  const platformLimitImageCDNSummaries = platformLimitSummaries.filter(item =>
    [`IMAGE_CDN_REQUESTS`].includes(item.name)
  )

  const hasOutdatedSourcePlugin = siteDependencies
    .filter(({ name }) =>
      supportedImageCdnPlugins.some(plugin => name.includes(plugin.name))
    )
    .some(({ installedVersion, name }) => {
      const plugin = supportedImageCdnPlugins.find(plugin =>
        name.includes(plugin.name)
      )
      if (plugin) {
        return semver.satisfies(
          semver.coerce(installedVersion) || ``,
          plugin.version
        )
      }
      return false
    })

  return (
    <div css={wrapperStyle}>
      <div css={innerWrapperStyle}>
        <ToggleCheckbox
          key={"imageCdnEnabled"}
          id="imageCdnEnabled"
          label={
            <React.Fragment>
              {text.labels.enableImageCdnText}
              <Spacer direction="horizontal" size={3} />
              <Link
                href={`https://gatsby.dev/site-settings-enable-image-cdn-info`}
                title={text.labels.learnAboutImageCdn}
              >
                <MdInfoOutline />
              </Link>
            </React.Fragment>
          }
          hint={
            <React.Fragment>
              {text.messages.imagesAtEdge}{" "}
              {!isCompatibleGatsbyVersion && (
                <FormattedMessage<never, "link">
                  message={text.messages.requiresGatsby4}
                  tags={{
                    link: function (content) {
                      return (
                        <Link
                          href={`https://gatsby.dev/site-settings-enable-image-cdn-read-more`}
                          variant="SIMPLE"
                        >
                          {content} <MdArrowForward />
                        </Link>
                      )
                    },
                  }}
                />
              )}
            </React.Fragment>
          }
          onChange={toggleImageCdn}
          checked={imageCdnEnabled}
          disabled={!isCompatibleGatsbyVersion}
        />
        {flags.imageCdnUsageToggle &&
          platformLimitImageCDNSummaries.length > 0 && (
            <div css={{ flexShrink: 0 }}>
              <ImageCdnUsage
                imageCdnUsageSummary={platformLimitImageCDNSummaries[0]}
              />
            </div>
          )}
      </div>
      {imageCdnEnabled && satisfiesDependencyRange && (
        <Notification
          Icon={MdInfo}
          variant="SECONDARY"
          tone="SUCCESS"
          showDismissButton={true}
          isOpened={notificationVisible}
          onDismissButtonClick={hideNotification}
          content={
            <React.Fragment>
              {text.messages.imageCdnEncryptionSuccessBannerText}
            </React.Fragment>
          }
        />
      )}
      {imageCdnEnabled && !satisfiesDependencyRange && (
        <Notification
          Icon={MdInfo}
          variant="SECONDARY"
          tone="DANGER"
          showDismissButton={true}
          isOpened={notificationVisible}
          onDismissButtonClick={hideNotification}
          content={
            <React.Fragment>
              {text.messages.imageCdnEncryptionFailureBannerText}
            </React.Fragment>
          }
        />
      )}
      {!imageCdnEnabled &&
        isCompatibleGatsbyVersion &&
        !hasOutdatedSourcePlugin && (
          <Notification
            Icon={MdInfo}
            variant="SECONDARY"
            tone="WARNING"
            showDismissButton={true}
            isOpened={notificationVisible}
            onDismissButtonClick={hideNotification}
            content={
              <React.Fragment>
                {text.messages.bannerText}&nbsp;{" "}
                <Link
                  variant="SIMPLE"
                  to="https://gatsby.dev/site-settings-enable-image-cdn-read-more"
                >
                  {text.messages.readMore} <MdArrowForward />
                </Link>
              </React.Fragment>
            }
            css={(theme: Theme) => ({
              padding: `${theme.space[5]} ${theme.space[3]} ${theme.space[5]} ${theme.space[5]}`,
            })}
          />
        )}
      {errorAlert}
    </div>
  )
}
